import "./style.scss";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Paper, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import DoubleArrow from "assets/img/svg/doubleArrow";
import DeletePaymentType from "components/Payment/ModalContent/DeletePaymentType";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ModalBase from "components/shared/ModalBase";
import TextButton from "components/shared/TextButton";
import { BodyHighlight } from "helpers/TypographyComponents";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IPaymentInfo, IPaymentResourceInfo } from "store/paymentSlice";
import { useAppSelector } from "store/store";

import variables from "../../../../assets/styles/_variables.module.scss";

const resourceLabelStyles = {
    minWidth: "200px",
    maxWidth: "700px",
    display: "inline-block",
} as const;

function isPaymentResourceDeleted(item: IPaymentResourceInfo, payment: IPaymentInfo): boolean {
    return item.isResourceDeleted ?? payment.deletedResources?.includes(item.id ?? -1) ?? false;
}

export function paymentTypePieceNumber(payment: Pick<IPaymentInfo, "allResources">): string {
    const numbers = [
        ...new Set(
            payment.allResources
                .map((item) => item.accountingPieceNumber)
                .filter((number): number is string => Boolean(number)),
        ),
    ];
    return numbers.join(", ") || "-";
}

function formatPaymentResourceLabel(item: IPaymentResourceInfo, payment: IPaymentInfo, deletedLabel: string) {
    const isDeleted = isPaymentResourceDeleted(item, payment);

    if (!isDeleted) {
        return <span style={resourceLabelStyles}>{item.titleName}</span>;
    }

    if (item.titleName) {
        return (
            <span style={resourceLabelStyles}>
                {item.titleName}
                <span style={{ fontStyle: "italic" }}>{` — ${deletedLabel}`}</span>
            </span>
        );
    }

    return <span style={{ ...resourceLabelStyles, fontStyle: "italic" }}>{deletedLabel}</span>;
}

export function formatPublisherPaymentNature(
    payment: Pick<IPaymentInfo, "type" | "isForfait">,
    t: (key: string) => string,
) {
    if (payment.type === 2) return t("Payment.Copyright");
    if (payment.type === 3) return t("Payment.Partial");
    return payment.isForfait ? t("Payment.Forfait") : t("Payment.Advance");
}

interface IPublisherTable {
    paymentData: IPaymentInfo[];
    beneficiaryName: string;
    paymentsCanBeDeleted: boolean;
    paymentLotId: number | undefined | null;
    closeDrawer: () => void;
}

const PublisherPaymentDetails = ({
    paymentData: _paymentData,
    beneficiaryName,
    paymentsCanBeDeleted,
    paymentLotId,
    closeDrawer,
}: IPublisherTable) => {
    const [t] = useTranslation();

    const { periods } = useAppSelector((state) => state.period);
    const [modalType, setModalType] = useState("");
    const [currentPayment, setCurrentPayment] = useState<IPaymentInfo>();
    const [paymentData, setPaymentData] = useState<IPaymentInfo[]>(_paymentData);

    const handleDeletePaymentType = (payment: IPaymentInfo) => {
        setCurrentPayment(payment);
        setModalType("delete");
    };

    const handleShowMore = (payment: any) => {
        const newData = paymentData.map((item) => {
            if (payment.id === item.id) {
                return { ...item, showMore: !payment.showMore };
            } else {
                return item;
            }
        });

        setPaymentData(newData);
    };

    const hasOnlyAdvance = paymentData.every((item) => item.type === 1);
    const hasOnlyPartial = paymentData.every((item) => item.type === 3);

    const columns: LastElementTableColumns[] = [
        {
            id: "type",
            label: t("Payment.Nature"),
            align: "left",
            format: (_, __, payment: IPaymentInfo) => <span>{formatPublisherPaymentNature(payment, t)}</span>,
        },
        {
            id: "periodId",
            label: t("Payment.Period"),
            format: (periodId: number) => {
                const currentPeriod = periods.find((period) => period.id === periodId);
                return currentPeriod ? (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            justifyContent: "center",
                        }}
                    >
                        {formatLocaleDateString({
                            date: currentPeriod.periodStart,
                            options: { numeric: true, timeZone: "UTC" },
                        })}
                        <DoubleArrow color={variables["foreground"]} height={"15"} />
                        {formatLocaleDateString({
                            date: currentPeriod.periodEnd,
                            options: { numeric: true, timeZone: "UTC" },
                        })}
                    </div>
                ) : (
                    <span>{"-"}</span>
                );
            },
        },
        {
            id: "paymentDate",
            label: t("Payment.Date"),
            format: (date: Date) => (
                <span style={{ display: "inline-block", minWidth: "290px" }}>
                    {date ? formatLocaleDateString({ date, options: { numeric: true, timeZone: "UTC" } }) : "-"}
                </span>
            ),
        },
        {
            id: "accountingPieceNumber",
            label: t("Payment.AccountingPieceNumber"),
            align: "left",
            format: (_, __, payment: IPaymentInfo) => (
                <span style={{ display: "inline-block", minWidth: "110px" }}>{paymentTypePieceNumber(payment)}</span>
            ),
        },
        {
            id: "totalValue",
            label: t("Payment.Amount"),
            align: "right",
            format: (amount: number) => (
                <span>
                    {formatNumber((amount || 0) / 100, {
                        isCurrency: true,
                        always2Digit: true,
                    })}
                </span>
            ),
        },
        {
            id: "action",
            label: "",
            align: "right",
            hidden: !paymentsCanBeDeleted,
            format: (_, __, payment: IPaymentInfo) => (
                <TextButton text={t("PartialPayment.Delete")} handleClick={() => handleDeletePaymentType(payment)} />
            ),
        },
        {
            id: "showMore",
            label: "",
            align: "right",
            hidden: hasOnlyPartial,
            format: (_, __, currentRow) =>
                currentRow.showMore ? (
                    <ExpandLessIcon className={"expand-icon"} onClick={() => handleShowMore(currentRow)} />
                ) : (
                    <ExpandMoreIcon className={"expand-icon"} onClick={() => handleShowMore(currentRow)} />
                ),
        },
    ];

    const handleDeleteModalClose = (hasOnlyOnePayment: boolean) => {
        setModalType("");
        if (hasOnlyOnePayment) {
            closeDrawer();
        }
    };

    const collapseHeadStyles = { borderBottom: "none", paddingBlock: "0px", fontFamily: "Poppins" };
    const collapseBodyStyles = { fontFamily: "Poppins", paddingBlock: "2px", fontSize: "12px", borderBottom: "none" };

    /**
     * Pretty dirty css but only way to align columns :))
     */
    const formatCollapse = (payment: IPaymentInfo) => {
        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell
                            style={{
                                ...collapseHeadStyles,
                                minWidth: paymentsCanBeDeleted ? "625px" : hasOnlyAdvance ? "695px" : "715px",
                            }}
                            colSpan={3}
                        >
                            <BodyHighlight style={{ fontSize: "12px", minWidth: "700px" }}>
                                {t("PartialPayment.RoyaltiesDetails")}
                            </BodyHighlight>
                        </TableCell>
                        <TableCell style={{ ...collapseHeadStyles, minWidth: "110px" }}></TableCell>
                        <TableCell
                            style={{ ...collapseHeadStyles, minWidth: paymentsCanBeDeleted ? "150px" : "160px" }}
                        ></TableCell>
                        {paymentsCanBeDeleted && <TableCell style={{ ...collapseHeadStyles }}></TableCell>}
                        <TableCell style={{ ...collapseHeadStyles }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {payment.allResources.map((item, idx) => {
                        const arrayLength = payment.allResources.length;
                        const isLastRow = arrayLength === 1 || idx === payment.allResources.length - 1;
                        return (
                            <TableRow className="beneficiary-payments-collapse" key={`${payment.name}-${idx}`}>
                                <TableCell
                                    style={{
                                        ...collapseBodyStyles,
                                        maxWidth: "700px",
                                        paddingBottom: isLastRow ? "15px" : "2px",
                                    }}
                                    colSpan={3}
                                >
                                    {formatPaymentResourceLabel(item, payment, t("PartialPayment.DeletedResource"))}
                                </TableCell>
                                <TableCell
                                    style={{
                                        ...collapseBodyStyles,
                                        minWidth: "110px",
                                        paddingBottom: isLastRow ? "15px" : "2px",
                                    }}
                                />
                                <TableCell
                                    style={{
                                        ...collapseBodyStyles,
                                        minWidth: paymentsCanBeDeleted ? "150px" : "160px",
                                        paddingBottom: isLastRow ? "15px" : "2px",
                                    }}
                                    align="right"
                                >
                                    {formatNumber((item.netToPay ?? 0) / 100, { isCurrency: true, always2Digit: true })}
                                </TableCell>
                                {paymentsCanBeDeleted && (
                                    <TableCell style={{ ...collapseBodyStyles }}>
                                        <span
                                            style={{
                                                display: "inline-block",
                                                minWidth: "130px",
                                                paddingBottom: isLastRow ? "15px" : "2px",
                                            }}
                                        ></span>
                                    </TableCell>
                                )}
                                <TableCell style={{ ...collapseBodyStyles }}>
                                    <span
                                        style={{
                                            display: "inline-block",
                                            minWidth: "77px",
                                            paddingBottom: isLastRow ? "15px" : "2px",
                                        }}
                                    ></span>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    };

    return (
        <Fragment>
            <ModalBase
                open={modalType === "delete"}
                onClose={() => setModalType("")}
                modalTitle={t("PartialPayment.DeletePayment")}
            >
                <DeletePaymentType
                    paymentsLength={paymentData.length}
                    payment={currentPayment}
                    beneficiaryName={beneficiaryName}
                    paymentLotId={paymentLotId}
                    handleClose={handleDeleteModalClose}
                />
            </ModalBase>
            <Paper
                sx={{
                    padding: 0,
                    width: "100%",
                    overflow: "hidden",
                    marginBottom: "20px",
                    border: "none",
                    borderRadius: variables["border-raduis"],
                    boxShadow: "none",
                }}
            >
                <LastElementTable
                    data={paymentData}
                    columns={columns}
                    max={-1}
                    type="paymentInfo"
                    collapseContent={(row) => formatCollapse(row)}
                    collapseOn={(row) => (row.type !== 3 ? "showMore" : "")}
                    tableClassName="publisher-payment-details-table"
                    onRowClick={(_, __, ___, payment) => (payment.type !== 3 ? handleShowMore(payment) : undefined)}
                />
            </Paper>
        </Fragment>
    );
};

export default PublisherPaymentDetails;
